const ANNOTATION_EDIT_RUNTIME_LOG_KEY = '__VERASCOPE_ANNOTATION_EDIT_DEBUG__';
const ANNOTATION_EDIT_CONSOLE_LOG_STORAGE_KEY = 'verascope-debug-annotation-edit-logs';
const ANNOTATION_EDIT_CONSOLE_LOG_QUERY_PARAM = 'debugAnnotationEditLogs';
const ANNOTATION_EDIT_RUNTIME_TOGGLE_KEY = '__VERASCOPE_ENABLE_ANNOTATION_EDIT_DEBUG__';
const MAX_RUNTIME_LOG_ENTRIES = 200;
const ANNOTATION_EDIT_DEBUG_STATUS_KEY = '__VERASCOPE_ANNOTATION_EDIT_DEBUG_STATUS__';
const ANNOTATION_EDIT_DEBUG_ENABLE_LOG_KEY = '__VERASCOPE_ANNOTATION_EDIT_DEBUG_ENABLE_LOGGED__';

interface IAnnotationEditDebugEntry {
    event: string,
    payload: Record<string, unknown>,
    timestamp: string,
}

interface IAnnotationEditDebugGateOptions {
    search?: string,
    storedValue?: string | null,
    envValue?: string,
    isDevelopment?: boolean,
    runtimeToggle?: boolean | null,
}

interface IAnnotationEditDebugStatus extends IAnnotationEditDebugGateOptions {
    enabled: boolean,
    source: 'query' | 'runtime' | 'storage' | 'env' | 'none',
}

function canUseWindow(): boolean {
    return typeof window !== 'undefined';
}

/**
 * Decide whether annotation-edit logs should emit for the current context.
 *
 * Development builds can enable logs via query params, a persisted localStorage
 * flag, or a one-off window toggle. Production keeps those ad hoc toggles off
 * and still relies on the explicit env flag.
 */
export function shouldEnableAnnotationEditConsoleLogs({
    search = '',
    storedValue = null,
    envValue,
    isDevelopment = false,
    runtimeToggle = null,
}: IAnnotationEditDebugGateOptions): boolean {
    if (isDevelopment) {
        let queryValue: string | null = null;

        try {
            queryValue = new URLSearchParams(search).get(ANNOTATION_EDIT_CONSOLE_LOG_QUERY_PARAM);
        } catch (_error) {
            queryValue = null;
        }

        // Query params win so one tab can force logs on or off without
        // rewriting the broader localStorage or env default.
        if (queryValue === '1') return true;
        if (queryValue === '0') return false;

        if (typeof runtimeToggle === 'boolean') return runtimeToggle;
        if (storedValue === '1') return true;
        if (storedValue === '0') return false;
    }

    return envValue === 'true';
}

/**
 * Preserve the winning gate source so the browser can report why annotation
 * edit logs are currently enabled or disabled.
 */
export function resolveAnnotationEditDebugStatus({
    search = '',
    storedValue = null,
    envValue,
    isDevelopment = false,
    runtimeToggle = null,
}: IAnnotationEditDebugGateOptions): IAnnotationEditDebugStatus {
    if (isDevelopment) {
        let queryValue: string | null = null;

        try {
            queryValue = new URLSearchParams(search).get(ANNOTATION_EDIT_CONSOLE_LOG_QUERY_PARAM);
        } catch (_error) {
            queryValue = null;
        }

        if (queryValue === '1') {
            return {
                enabled: true,
                source: 'query',
                search,
                storedValue,
                envValue,
                isDevelopment,
                runtimeToggle,
            };
        }

        if (queryValue === '0') {
            return {
                enabled: false,
                source: 'query',
                search,
                storedValue,
                envValue,
                isDevelopment,
                runtimeToggle,
            };
        }

        if (typeof runtimeToggle === 'boolean') {
            return {
                enabled: runtimeToggle,
                source: 'runtime',
                search,
                storedValue,
                envValue,
                isDevelopment,
                runtimeToggle,
            };
        }

        if (storedValue === '1') {
            return {
                enabled: true,
                source: 'storage',
                search,
                storedValue,
                envValue,
                isDevelopment,
                runtimeToggle,
            };
        }

        if (storedValue === '0') {
            return {
                enabled: false,
                source: 'storage',
                search,
                storedValue,
                envValue,
                isDevelopment,
                runtimeToggle,
            };
        }
    }

    if (envValue === 'true') {
        return {
            enabled: true,
            source: 'env',
            search,
            storedValue,
            envValue,
            isDevelopment,
            runtimeToggle,
        };
    }

    return {
        enabled: false,
        source: 'none',
        search,
        storedValue,
        envValue,
        isDevelopment,
        runtimeToggle,
    };
}

function syncAnnotationEditDebugStatus(status: IAnnotationEditDebugStatus): void {
    if (!canUseWindow()) {
        return;
    }

    (
        window as typeof window & {
            [ANNOTATION_EDIT_DEBUG_STATUS_KEY]?: IAnnotationEditDebugStatus,
        }
    )[ANNOTATION_EDIT_DEBUG_STATUS_KEY] = status;
}

function maybeLogAnnotationEditDebugEnabled(status: IAnnotationEditDebugStatus): void {
    if (!canUseWindow() || !status.enabled) {
        return;
    }

    const debugWindow = window as typeof window & {
        [ANNOTATION_EDIT_DEBUG_ENABLE_LOG_KEY]?: boolean,
    };

    if (debugWindow[ANNOTATION_EDIT_DEBUG_ENABLE_LOG_KEY]) {
        return;
    }

    debugWindow[ANNOTATION_EDIT_DEBUG_ENABLE_LOG_KEY] = true;

    // eslint-disable-next-line no-console
    console.info('[annotation-edit] debug enabled', status);
}

/**
 * Resolve the current runtime gate on every log attempt so turning the debug
 * switch on in the browser takes effect immediately without a rebuild.
 */
export function isAnnotationEditDebugEnabled(): boolean {
    let nextStatus: IAnnotationEditDebugStatus;

    if (!canUseWindow()) {
        nextStatus = resolveAnnotationEditDebugStatus({
            envValue: process.env.REACT_APP_ENABLE_ANNOTATION_EDIT_LOGS,
            isDevelopment: process.env.NODE_ENV === 'development',
        });
        return nextStatus.enabled;
    }

    let storedValue: string | null = null;
    try {
        storedValue = window.localStorage.getItem(ANNOTATION_EDIT_CONSOLE_LOG_STORAGE_KEY);
    } catch (_error) {
        storedValue = null;
    }

    const runtimeToggle = (
        window as typeof window & {
            [ANNOTATION_EDIT_RUNTIME_TOGGLE_KEY]?: boolean,
        }
    )[ANNOTATION_EDIT_RUNTIME_TOGGLE_KEY];

    nextStatus = resolveAnnotationEditDebugStatus({
        search: window.location.search,
        storedValue,
        envValue: process.env.REACT_APP_ENABLE_ANNOTATION_EDIT_LOGS,
        isDevelopment: process.env.NODE_ENV === 'development',
        runtimeToggle: typeof runtimeToggle === 'boolean' ? runtimeToggle : null,
    });

    syncAnnotationEditDebugStatus(nextStatus);
    maybeLogAnnotationEditDebugEnabled(nextStatus);

    return nextStatus.enabled;
}

/**
 * Appends a capped annotation-edit debug entry to the runtime buffer.
 *
 * Keeping the buffer local to this logger makes it easy to inspect the most
 * recent annotation-edit transitions from the console without mixing them into
 * unrelated debug streams.
 */
function appendRuntimeEntry(entry: IAnnotationEditDebugEntry): void {
    const runtimeEntries = (
        globalThis as typeof globalThis & {
            [ANNOTATION_EDIT_RUNTIME_LOG_KEY]?: IAnnotationEditDebugEntry[],
        }
    )[ANNOTATION_EDIT_RUNTIME_LOG_KEY];

    if (Array.isArray(runtimeEntries)) {
        runtimeEntries.push(entry);
        if (runtimeEntries.length > MAX_RUNTIME_LOG_ENTRIES) {
            runtimeEntries.splice(0, runtimeEntries.length - MAX_RUNTIME_LOG_ENTRIES);
        }
        return;
    }

    (
        globalThis as typeof globalThis & {
            [ANNOTATION_EDIT_RUNTIME_LOG_KEY]?: IAnnotationEditDebugEntry[],
        }
    )[ANNOTATION_EDIT_RUNTIME_LOG_KEY] = [entry];
}

/**
 * Emits read-mode annotation edit diagnostics behind a dedicated env flag.
 *
 * This keeps the logs off by default while still giving us a console stream and
 * a small window-scoped ring buffer when we need to debug blank editor shells.
 */
export default function logAnnotationEditDebug(
    event: string,
    payload?: Record<string, unknown>,
): void {
    if (!isAnnotationEditDebugEnabled()) {
        return;
    }

    const nextPayload = payload || {};
    const nextEntry: IAnnotationEditDebugEntry = {
        event,
        payload: nextPayload,
        timestamp: new Date().toISOString(),
    };

    appendRuntimeEntry(nextEntry);

    // eslint-disable-next-line no-console
    console.info('[annotation-edit]', event, nextPayload);
}

export {
    ANNOTATION_EDIT_CONSOLE_LOG_QUERY_PARAM,
    ANNOTATION_EDIT_CONSOLE_LOG_STORAGE_KEY,
    ANNOTATION_EDIT_DEBUG_STATUS_KEY,
    ANNOTATION_EDIT_RUNTIME_LOG_KEY,
    ANNOTATION_EDIT_RUNTIME_TOGGLE_KEY,
};

if (canUseWindow()) {
    const runtimeToggle = (
        window as typeof window & {
            [ANNOTATION_EDIT_RUNTIME_TOGGLE_KEY]?: boolean,
        }
    )[ANNOTATION_EDIT_RUNTIME_TOGGLE_KEY];
    let storedValue: string | null = null;

    try {
        storedValue = window.localStorage.getItem(ANNOTATION_EDIT_CONSOLE_LOG_STORAGE_KEY);
    } catch (_error) {
        storedValue = null;
    }

    const nextStatus = resolveAnnotationEditDebugStatus({
        search: window.location.search,
        storedValue,
        envValue: process.env.REACT_APP_ENABLE_ANNOTATION_EDIT_LOGS,
        isDevelopment: process.env.NODE_ENV === 'development',
        runtimeToggle: typeof runtimeToggle === 'boolean' ? runtimeToggle : null,
    });

    syncAnnotationEditDebugStatus(nextStatus);
    maybeLogAnnotationEditDebugEnabled(nextStatus);
}
